import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, Search, CheckSquare, Square, Trash2, FileDown } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useTableTools } from "@/lib/useTableTools";
import { money, formatDate } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ExpensesPanel } from "@/components/ExpensesPanel";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const SOURCE_LABEL = { quote: "Quote", order: "Order", manual: "Manual", payment: "Payment", expense: "Expense" };

export default function Invoices() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [statements, setStatements] = useState([]);
  const [tab, setTab] = useState("incoming");
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === "admin";
  const tools = useTableTools(rows, ["number", "client_name", "order_number", "source", "status"]);

  const load = () => {
    api.get("/invoices").then(({ data }) => setRows(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail))).finally(() => setLoading(false));
    api.get("/invoices/partner-statements").then(({ data }) => setStatements(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  // Tab datasets (incoming = client invoices into the platform; outgoing/expenses kept separate).
  const isManual = (r) => r.source === "manual";
  const isOutgoing = (r) => r.direction === "outgoing" || r.source === "partner_invoice";
  const incoming = tools.filtered.filter((r) => !isManual(r) && !isOutgoing(r));
  const manual = tools.filtered.filter(isManual);
  const tabRows = tab === "manual" ? manual : incoming;

  const bulkDelete = async () => {
    const ids = [...tools.selected];
    if (!ids.length || !window.confirm(`${t("common.delete")} ${ids.length}?`)) return;
    try { await api.post("/invoices/bulk-delete", { ids }); toast.success("✓"); tools.clear(); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const exportCsv = () => {
    const sel = tools.selectedRows.length ? tools.selectedRows : tools.filtered;
    const header = ["number", "client_name", "order_number", "source", "order_total", "total", "payment_status", "issued_at"];
    const csv = [header.join(",")].concat(sel.map((r) => header.map((h) => `"${(r[h] ?? "").toString().replace(/"/g, '""')}"`).join(","))).join("\n");
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" })); a.download = "invoices.csv"; a.click();
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div>
        <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight text-slate-900">{t("menu.invoices")}</h1>
        <p className="text-sm text-slate-500 mt-1">{t("inv.subtitle")}</p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList data-testid="invoices-tabs" className="max-w-full justify-start overflow-x-auto">
          <TabsTrigger value="incoming" data-testid="tab-incoming">Fatture in entrata ({incoming.length})</TabsTrigger>
          <TabsTrigger value="statements" data-testid="tab-statements">Pagamenti partner ({statements.length})</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses">Spese</TabsTrigger>
          <TabsTrigger value="manual" data-testid="tab-manual">Manuali ({manual.length})</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab !== "statements" && tab !== "expenses" && (
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={tools.query} onChange={(e) => tools.setQuery(e.target.value)} placeholder={t("common.search")} className="pl-9" data-testid="invoices-search" />
        </div>
        <Button variant="outline" size="sm" onClick={tools.selectAllVisible} data-testid="invoices-select-all"><CheckSquare size={15} className="mr-1.5" /> {t("common.selectAll")}</Button>
        <Button variant="outline" size="sm" onClick={tools.clear} data-testid="invoices-clear"><Square size={15} className="mr-1.5" /> {t("common.deselect")}</Button>
      </div>
      )}

      {tab !== "statements" && tab !== "expenses" && tools.selected.size > 0 && (
        <div className="flex items-center gap-2 flex-wrap bg-blue-900 text-white rounded-xl px-4 py-2.5" data-testid="invoices-bulk-bar">
          <span className="text-sm font-medium">{tools.selected.size} {t("common.selected")}</span>
          <div className="h-5 w-px bg-white/20 mx-1" />
          <Button size="sm" variant="secondary" className="h-8" onClick={exportCsv} data-testid="invoices-bulk-export"><FileDown size={14} className="mr-1" /> {t("bulk.export")}</Button>
          {isAdmin && <Button size="sm" variant="destructive" className="h-8" onClick={bulkDelete} data-testid="invoices-bulk-delete"><Trash2 size={14} className="mr-1" /> {t("bulk.delete")}</Button>}
          <Button size="sm" variant="ghost" className="h-8 ml-auto text-white hover:bg-white/10" onClick={tools.clear}>{t("common.cancelSelection")}</Button>
        </div>
      )}

      {tab === "expenses" ? (
        <ExpensesPanel />
      ) : tab === "statements" ? (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto" data-testid="statements-table">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase text-slate-500">Ordine</TableHead>
              <TableHead className="text-xs uppercase text-slate-500">Partner</TableHead>
              <TableHead className="text-xs uppercase text-slate-500">Payout (in uscita)</TableHead>
              <TableHead className="text-xs uppercase text-slate-500">Stato</TableHead>
              {isAdmin && <TableHead className="text-xs uppercase text-slate-500">Profitto</TableHead>}
              <TableHead className="text-xs uppercase text-slate-500 text-right">{t("common.actions")}</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {statements.length === 0 ? <TableRow><TableCell colSpan={isAdmin ? 6 : 5} className="text-center py-10 text-slate-400">{t("common.none")}</TableCell></TableRow>
                : statements.map((s) => (
                  <TableRow key={s.order_id} data-testid="statement-row">
                    <TableCell className="font-medium text-slate-800 cursor-pointer" onClick={() => navigate(`/orders/${s.order_id}`)}>{s.order_number || "—"}</TableCell>
                    <TableCell>{s.partner_name || "—"}</TableCell>
                    <TableCell className="font-medium text-rose-600">-{money(s.amount, s.currency)}</TableCell>
                    <TableCell><StatusBadge status={s.status || "pending"} /></TableCell>
                    {isAdmin && <TableCell className="text-emerald-700 font-medium">{s.platform_profit != null ? money(s.platform_profit, s.currency) : "—"}</TableCell>}
                    <TableCell className="text-right"><Button size="icon" variant="ghost" data-testid="statement-view-btn" onClick={() => navigate(`/orders/${s.order_id}`)}><Eye size={15} /></Button></TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      ) : (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead className="w-10"></TableHead>
            <TableHead className="text-xs uppercase text-slate-500">{t("common.number")}</TableHead>
            <TableHead className="text-xs uppercase text-slate-500">{t("common.client")}</TableHead>
            <TableHead className="text-xs uppercase text-slate-500">{t("inv.order")}</TableHead>
            <TableHead className="text-xs uppercase text-slate-500">{t("inv.source")}</TableHead>
            <TableHead className="text-xs uppercase text-slate-500">{t("inv.orderTotal")}</TableHead>
            <TableHead className="text-xs uppercase text-slate-500">{t("inv.invoiceTotal")}</TableHead>
            <TableHead className="text-xs uppercase text-slate-500">{t("order.payment")}</TableHead>
            <TableHead className="text-xs uppercase text-slate-500">{t("inv.created")}</TableHead>
            <TableHead className="text-xs uppercase text-slate-500 text-right">{t("common.actions")}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={10} className="text-center py-10 text-slate-400">{t("common.loading")}</TableCell></TableRow>
              : tabRows.length === 0 ? <TableRow><TableCell colSpan={10} className="text-center py-10 text-slate-400">{t("common.none")}</TableCell></TableRow>
              : tabRows.map((inv) => (
                <TableRow key={inv.id} data-testid="invoice-row">
                  <TableCell><Checkbox checked={tools.selected.has(inv.id)} onCheckedChange={() => tools.toggle(inv.id)} data-testid={`invoice-check-${inv.id}`} /></TableCell>
                  <TableCell className="font-medium text-slate-800 cursor-pointer" onClick={() => navigate(`/invoices/${inv.id}`)}>{inv.number}</TableCell>
                  <TableCell>{inv.client_name || "—"}</TableCell>
                  <TableCell className="text-sm text-slate-500">{inv.order_number || "—"}</TableCell>
                  <TableCell><span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">{SOURCE_LABEL[inv.source] || inv.source || "—"}</span></TableCell>
                  <TableCell className="text-sm">{inv.order_total != null ? money(inv.order_total, inv.currency) : "—"}</TableCell>
                  <TableCell className="font-medium">{money(inv.total, inv.currency)}</TableCell>
                  <TableCell>
                    {inv.payment_status === "paid"
                      ? <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200" data-testid="invoice-paid-badge">{t("order.paid")}</span>
                      : <StatusBadge status={inv.status} />}
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">{formatDate(inv.issued_at || inv.created_at)}</TableCell>
                  <TableCell className="text-right"><Button size="icon" variant="ghost" data-testid="invoice-view-btn" onClick={() => navigate(`/invoices/${inv.id}`)}><Eye size={15} /></Button></TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
      )}
    </div>
  );
}
